import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/landing/Footer';

const TermsAndConditions = () => {
  const lastUpdated = 'November 25, 2025';
  const effectiveDate = 'November 25, 2025';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img
                src="/img/logo-ag.png"
                alt="AgentGarden Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-gray-900">AgentGarden</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 mb-2">Last Updated: {lastUpdated}</p>
          <p className="text-gray-600 mb-8">Effective Date: {effectiveDate}</p>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction and Acceptance</h2>
              <p>
                Welcome to AgentGarden, operated by <strong>Switchr Technologies</strong> ("Company," "we," "us," or "our").
                These Terms and Conditions ("Terms") govern your access to and use of our AI agent platform, website,
                and related services (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be
                bound by these Terms, our <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>,
                and all applicable laws and regulations. If you do not agree to these Terms, you must not access or use our Services.
              </p>
              <p>
                These Terms comply with applicable laws in the European Union (including GDPR and EU AI Act),
                United Kingdom (UK GDPR and Data Protection Act 2018), and India (Digital Personal Data Protection Act 2023
                and Information Technology Act 2000).
              </p>
            </section>

            {/* Company Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Company Information</h2>
              <div className="bg-gray-100 p-6 rounded-lg">
                <p><strong>Company Name:</strong> Switchr Technologies</p>
                <p><strong>Registered Address:</strong> A221-224, Sunder Nagar, Near 200 Ft Bypass, Jaipur (Towards Mansarover), Ajmer Road, Jaipur-302021, Rajasthan, India</p>
                <p><strong>Toll Free:</strong> 1800 123 7008</p>
                <p><strong>Email:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></p>
              </div>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Description of Services</h2>
              <p>
                AgentGarden provides a SaaS platform for building, deploying, and managing AI agents through
                templated agentic workflows. Our Services include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Visual node-based workflow editor for designing AI agent behaviors</li>
                <li>Pre-built workflow templates for common business automation tasks</li>
                <li>Integration connectors with third-party applications (Gmail, Slack, Google Calendar, HubSpot, etc.)</li>
                <li>AI-powered scheduling and booking management</li>
                <li>Human-in-the-loop approval mechanisms for AI decisions</li>
                <li>Agent management dashboard with monitoring and logging capabilities</li>
              </ul>
            </section>

            {/* AI-Specific Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. AI Agent Terms and Responsible AI Use</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Nature of AI Services</h3>
              <p>
                Our platform utilizes artificial intelligence and machine learning technologies to provide automated
                workflow and agent capabilities. You acknowledge and understand that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>AI-generated outputs may contain errors, inaccuracies, or biases and should be reviewed by qualified humans before critical decisions</li>
                <li>AI agents operate based on probabilistic models and may produce varying results for similar inputs</li>
                <li>The AI systems require appropriate human oversight, particularly for decisions affecting individuals' rights or significant outcomes</li>
                <li>We continuously work to improve AI accuracy and reduce biases, but cannot guarantee error-free performance</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Human Oversight Requirements (EU AI Act Compliance)</h3>
              <p>
                In accordance with the EU AI Act and ethical AI principles, users must:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintain meaningful human oversight over AI agent operations</li>
                <li>Review AI-generated decisions before implementation, especially for high-risk applications</li>
                <li>Implement human-in-the-loop approval workflows for critical business processes</li>
                <li>Report any AI malfunctions, biases, or concerning behaviors to us immediately</li>
                <li>Not use AI agents to make fully autonomous decisions in areas affecting fundamental rights</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Prohibited AI Uses</h3>
              <p>You agree NOT to use our AI Services for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Discriminatory profiling or decision-making based on protected characteristics (race, gender, religion, disability, etc.)</li>
                <li>Manipulation, deception, or exploiting vulnerabilities of individuals</li>
                <li>Real-time biometric identification in publicly accessible spaces (unless legally authorized)</li>
                <li>Social scoring or behavioral prediction for punitive purposes</li>
                <li>Generating deepfakes, synthetic media for fraud, or misleading content</li>
                <li>Automated decision-making that significantly affects individuals without human review</li>
                <li>Processing children's data without appropriate safeguards and parental consent</li>
                <li>Any activities that violate applicable laws, regulations, or ethical standards</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.4 Transparency and Explainability</h3>
              <p>
                We are committed to AI transparency. Where technically feasible, we provide:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Explanations of how AI agents process data and reach decisions</li>
                <li>Logging and audit trails of AI agent actions</li>
                <li>Clear disclosure when interactions are AI-generated</li>
                <li>Documentation of AI model capabilities and limitations</li>
              </ul>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Accounts and Registration</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Account Creation</h3>
              <p>
                To access certain features of our Services, you must create an account. When registering, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Be at least 18 years of age or the age of majority in your jurisdiction</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Account Security</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials.
                Notify us immediately at <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a> if
                you suspect unauthorized access to your account.
              </p>
            </section>

            {/* User Obligations */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. User Obligations and Acceptable Use</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Lawful Use</h3>
              <p>You agree to use the Services only for lawful purposes and in accordance with these Terms. You shall not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Use the Services for illegal, fraudulent, or harmful activities</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the integrity or performance of the Services</li>
                <li>Reverse engineer, decompile, or disassemble any part of our Services</li>
                <li>Use automated systems to scrape or extract data without permission</li>
                <li>Resell or redistribute our Services without authorization</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2 Content Standards</h3>
              <p>Any content you create, upload, or process through our Services must not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be defamatory, obscene, offensive, or promote hatred or violence</li>
                <li>Infringe intellectual property rights of others</li>
                <li>Contain personal data processed without proper legal basis</li>
                <li>Constitute spam or unsolicited commercial communications</li>
                <li>Violate any person's privacy or data protection rights</li>
              </ul>
            </section>

            {/* Data Protection */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Protection and Privacy</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Privacy Policy</h3>
              <p>
                Our collection and use of personal data is governed by our <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>,
                which is incorporated into these Terms by reference. By using our Services, you consent to such
                processing and warrant that all data provided by you is accurate.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.2 Data Processing Roles</h3>
              <p>
                <strong>For EU/UK Users:</strong> When you use our Services to process personal data of your end users or customers:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You act as the Data Controller for such data</li>
                <li>We act as the Data Processor processing data on your behalf</li>
                <li>A Data Processing Agreement (DPA) governs our processing activities, available upon request</li>
              </ul>
              <p className="mt-4">
                <strong>For Indian Users:</strong> Under the Digital Personal Data Protection Act 2023:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You act as the Data Fiduciary for data you collect through our Services</li>
                <li>We act as the Data Processor processing data based on your instructions</li>
                <li>You must ensure lawful collection and appropriate consent mechanisms</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.3 Cross-Border Data Transfers</h3>
              <p>
                Our Services may involve transfer of data across borders. We implement appropriate safeguards including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Standard Contractual Clauses (SCCs) for EU/UK data transfers</li>
                <li>Compliance with India's data localization requirements where applicable</li>
                <li>Encryption and security measures during data transit and at rest</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Intellectual Property Rights</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 Our Intellectual Property</h3>
              <p>
                The Services, including all content, features, functionality, software, code, designs, trademarks,
                and underlying technology, are owned by Switchr Technologies and protected by intellectual property laws.
                You receive a limited, non-exclusive, non-transferable license to use the Services for their intended purpose.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.2 Your Content</h3>
              <p>
                You retain ownership of content you create using our Services. By submitting content, you grant us a
                worldwide, non-exclusive, royalty-free license to use, process, and store such content solely for
                providing and improving our Services.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">8.3 AI-Generated Content</h3>
              <p>
                Content generated by AI agents using our Services may be subject to limitations on intellectual property
                protection under applicable laws. You are responsible for reviewing and ensuring appropriate use of
                AI-generated content.
              </p>
            </section>

            {/* Payment Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Payment Terms</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 Subscription Fees</h3>
              <p>
                Access to certain features requires a paid subscription. By subscribing, you agree to pay all
                applicable fees as described in our pricing page. Fees are non-refundable except as required by law.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">9.2 Billing</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                <li>All prices are exclusive of applicable taxes unless stated otherwise</li>
                <li>We reserve the right to change pricing with 30 days' notice</li>
                <li>Failure to pay may result in suspension or termination of Services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">9.3 Refund Policy</h3>
              <p>
                For EU/UK users: You have a 14-day right of withdrawal for online purchases, unless you have
                already accessed or used the Services. For Indian users: Refunds are processed in accordance
                with applicable consumer protection laws.
              </p>
            </section>

            {/* Third-Party Integrations */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Integrations and Services</h2>
              <p>
                Our Services integrate with third-party applications and services (e.g., Gmail, Slack, Google Calendar,
                Zoom, HubSpot). Your use of such integrations is subject to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The third party's terms of service and privacy policies</li>
                <li>Your responsibility to obtain necessary permissions and authorizations</li>
                <li>Understanding that we are not responsible for third-party services' availability, accuracy, or conduct</li>
              </ul>
              <p className="mt-4">
                We are not liable for any damages or losses arising from your use of third-party integrations.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Disclaimers and Limitation of Liability</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">11.1 Service Disclaimers</h3>
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The Services will be uninterrupted, error-free, or secure</li>
                <li>AI outputs will be accurate, complete, or suitable for any particular purpose</li>
                <li>Defects will be corrected in any specific timeframe</li>
                <li>The Services will meet your specific requirements</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">11.2 Limitation of Liability</h3>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SWITCHR TECHNOLOGIES SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, business opportunities, or goodwill</li>
                <li>Damages arising from AI agent errors, biases, or malfunctions</li>
                <li>Any amount exceeding fees paid by you in the 12 months preceding the claim</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">11.3 Consumer Rights</h3>
              <p>
                <strong>For EU/UK Users:</strong> Nothing in these Terms excludes or limits liability for death,
                personal injury caused by negligence, fraud, or any liability that cannot be excluded under
                applicable consumer protection laws.
              </p>
              <p className="mt-2">
                <strong>For Indian Users:</strong> Your statutory rights under the Consumer Protection Act 2019
                and other applicable laws remain unaffected.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Switchr Technologies and its officers, directors,
                employees, and agents from any claims, damages, losses, or expenses (including reasonable legal fees)
                arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the Services in violation of these Terms</li>
                <li>Your violation of any applicable laws or third-party rights</li>
                <li>Any content you create, submit, or process through the Services</li>
                <li>Your failure to maintain appropriate human oversight over AI agents</li>
                <li>Data breaches resulting from your negligence or security failures</li>
              </ul>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Termination</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">13.1 Termination by You</h3>
              <p>
                You may terminate your account at any time by contacting us or using account settings.
                Termination does not entitle you to refunds for pre-paid subscription periods.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">13.2 Termination by Us</h3>
              <p>We may suspend or terminate your access if:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You breach these Terms or our policies</li>
                <li>You fail to pay applicable fees</li>
                <li>Required by law or regulatory authority</li>
                <li>Your actions pose security risks or harm to others</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">13.3 Effect of Termination</h3>
              <p>
                Upon termination, your right to access the Services ceases immediately. We will retain your data
                for a reasonable period to comply with legal obligations and allow data export, after which it
                will be securely deleted.
              </p>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Dispute Resolution and Governing Law</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">14.1 Governing Law</h3>
              <p>
                <strong>For Indian Users:</strong> These Terms are governed by the laws of India. Disputes shall be
                subject to the exclusive jurisdiction of courts in Jaipur, Rajasthan.
              </p>
              <p className="mt-2">
                <strong>For EU Users:</strong> These Terms are governed by the laws of the user's country of residence.
                You may bring proceedings in courts of your country of residence or India.
              </p>
              <p className="mt-2">
                <strong>For UK Users:</strong> These Terms are governed by English law. Disputes may be brought in
                English courts or courts of your country of residence.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">14.2 Informal Resolution</h3>
              <p>
                Before initiating formal proceedings, we encourage you to contact us at
                <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline ml-1">hello@agentgarden.co</a> to
                attempt amicable resolution of any disputes.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">14.3 Alternative Dispute Resolution</h3>
              <p>
                <strong>For EU Users:</strong> You may use the European Commission's Online Dispute Resolution platform
                at <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">https://ec.europa.eu/odr</a>.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time to reflect changes in our Services, legal requirements,
                or business practices. When we make material changes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We will notify you via email or through the Services at least 30 days before changes take effect</li>
                <li>The updated Terms will be posted on our website with a new "Last Updated" date</li>
                <li>Continued use of Services after changes constitutes acceptance of new Terms</li>
                <li>If you disagree with changes, you may terminate your account before they take effect</li>
              </ul>
            </section>

            {/* General Provisions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. General Provisions</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">16.1 Entire Agreement</h3>
              <p>
                These Terms, together with our Privacy Policy and any other policies referenced herein, constitute
                the entire agreement between you and Switchr Technologies regarding the Services.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">16.2 Severability</h3>
              <p>
                If any provision of these Terms is found invalid or unenforceable, the remaining provisions shall
                continue in full force and effect.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">16.3 No Waiver</h3>
              <p>
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of
                such right or provision.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">16.4 Assignment</h3>
              <p>
                You may not assign or transfer your rights under these Terms without our prior written consent.
                We may assign our rights and obligations without restriction.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">16.5 Force Majeure</h3>
              <p>
                We shall not be liable for any failure or delay in performance due to causes beyond our reasonable
                control, including natural disasters, war, terrorism, pandemics, or government actions.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Us</h2>
              <p>
                For questions, concerns, or requests regarding these Terms, please contact us:
              </p>
              <div className="bg-gray-100 p-6 rounded-lg mt-4">
                <p><strong>Switchr Technologies</strong></p>
                <p>A221-224, Sunder Nagar, Near 200 Ft Bypass</p>
                <p>Jaipur (Towards Mansarover), Ajmer Road</p>
                <p>Jaipur-302021, Rajasthan, India</p>
                <p className="mt-4"><strong>Toll Free:</strong> 1800 123 7008</p>
                <p><strong>Email:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Acknowledgment</h2>
              <p>
                By using our Services, you acknowledge that you have read these Terms and Conditions,
                understand them, and agree to be bound by them. If you are entering into these Terms on
                behalf of an organization, you represent and warrant that you have the authority to bind
                that organization to these Terms.
              </p>
            </section>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
